import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import * as DocumentPicker from 'expo-document-picker';
import { useUser } from './LoginPersistence';
import { useCommunity } from '../contexts/CommunityContext';
import EditablePostGrid from './EditablePostGrid';
import { MoveModeDesktopNote } from './MoveModeDesktopNote';
import { CardInterestsDisplay } from './CardInterestsDisplay';
import { CardBgColorPicker } from './CardBgColorPicker';
import { DismissOverlay } from './DismissOverlay';
import {
  WIDGET_TYPES,
  TEMPLATES,
  CARD_BG_SWATCHES,
  mergeTemplateWithCurrent,
  sanitizeForFirestore,
  CELL_SIZE,
} from '../screens/CommunityPostCreate';
import { GRID_COLS, GRID_ROWS } from '../utils/postGridEngine';

export const PROFILE_CARD_EDITOR_BLUE = '#1075ae';

export function ProfileCardEditor({
  embedded = false,
  interestEmojis = [],
  interestItems = [],
  showInterestEmojis = false,
  onSaved,
  saveLabel = 'Save profile card',
}) {
  const { user: userData, setUser } = useUser();
  const { refreshEntities } = useCommunity();
  const [saving, setSaving] = useState(false);

  const initialItems = useMemo(
    () =>
      Array.from({ length: GRID_COLS * GRID_ROWS }, (_, i) => ({
        id: `cell-${i}`,
        type: 'text',
        value: '',
        x: i % GRID_COLS,
        y: Math.floor(i / GRID_COLS),
        w: 1,
        h: 1,
      })),
    []
  );

  const [items, setItems] = useState(initialItems);
  const [cardBackground, setCardBackground] = useState(null);
  const [showInterestsOnCard, setShowInterestsOnCard] = useState(true);
  const [cardBgPanelOpen, setCardBgPanelOpen] = useState(false);
  const [cardBgTab, setCardBgTab] = useState('color');
  const [uploadingCardBg, setUploadingCardBg] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [gridMode, setGridMode] = useState('move');
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [uploadingCellId, setUploadingCellId] = useState(null);

  const closePanels = useCallback(() => {
    setShowTemplates(false);
    setCardBgPanelOpen(false);
  }, []);

  const loadFromUserTemplate = useCallback(() => {
    const t = userData?.profileCardTemplate;
    if (t?.items && Array.isArray(t.items) && t.items.length > 0) {
      setItems(t.items);
    } else {
      setItems(initialItems);
    }
    setCardBackground(t?.cardBackground ?? null);
    setShowInterestsOnCard(t?.showInterestsOnCard !== false);
    undoStackRef.current = [];
    redoStackRef.current = [];
    setHistoryVersion((v) => v + 1);
  }, [userData?.profileCardTemplate, initialItems]);

  useEffect(() => {
    loadFromUserTemplate();
  }, [loadFromUserTemplate]);

  const setItemsWithHistory = useCallback((updater) => {
    setItems((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      undoStackRef.current.push(prev);
      if (undoStackRef.current.length > 50) undoStackRef.current.shift();
      redoStackRef.current = [];
      setHistoryVersion((v) => v + 1);
      return next;
    });
  }, []);

  const handleFileUpload = useCallback(
    async (id, cellType = 'file') => {
      try {
        setUploadingCellId(id);
        const acceptTypes =
          cellType === 'audio' ? ['audio/*'] : ['image/*', 'video/*', 'audio/*', 'application/pdf'];
        const result = await DocumentPicker.getDocumentAsync({
          type: acceptTypes,
          copyToCacheDirectory: true,
        });
        if (!result.canceled && result.assets && result.assets.length > 0) {
          const file = result.assets[0];
          const response = await fetch(file.uri);
          const fileBlob = await response.blob();
          const storage = getStorage();
          const uid = userData?.uid || 'anonymous';
          const storageRef = ref(storage, `profileCardMedia/${uid}/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, fileBlob);
          const downloadURL = await getDownloadURL(storageRef);
          let mimeType = file.mimeType;
          if (!mimeType) {
            if (/\.(mp4|webm|mov|avi|m4v)$/i.test(file.name || '')) mimeType = 'video/mp4';
            else if (/\.(mp3|wav|ogg|aac|flac|m4a)$/i.test(file.name || '')) mimeType = 'audio/mpeg';
            else if (/\.pdf$/i.test(file.name || '')) mimeType = 'application/pdf';
            else mimeType = 'image/*';
          }
          const value = JSON.stringify({ url: downloadURL, name: file.name, mimeType });
          setItemsWithHistory((prev) => prev.map((i) => (i.id === id ? { ...i, value } : i)));
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        Alert.alert('Upload failed', 'Failed to upload file. Please try again.');
      } finally {
        setUploadingCellId(null);
      }
    },
    [userData?.uid, setItemsWithHistory]
  );

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  const handleUndo = useCallback(() => {
    setItems((current) => {
      const previous = undoStackRef.current.pop();
      if (!previous) return current;
      redoStackRef.current.push(current);
      setHistoryVersion((v) => v + 1);
      return previous;
    });
  }, []);

  const handleRedo = useCallback(() => {
    setItems((current) => {
      const next = redoStackRef.current.pop();
      if (!next) return current;
      undoStackRef.current.push(current);
      setHistoryVersion((v) => v + 1);
      return next;
    });
  }, []);

  const handleCardBgUpload = useCallback(
    async (bgType) => {
      try {
        setUploadingCardBg(true);
        const acceptTypes = bgType === 'video' ? ['video/*'] : ['image/*'];
        const result = await DocumentPicker.getDocumentAsync({
          type: acceptTypes,
          copyToCacheDirectory: true,
        });
        if (!result.canceled && result.assets?.length > 0) {
          const file = result.assets[0];
          const response = await fetch(file.uri);
          const fileBlob = await response.blob();
          const storage = getStorage();
          const uid = userData?.uid || 'anonymous';
          const storageRef = ref(storage, `profileCardBg/${uid}/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, fileBlob);
          const url = await getDownloadURL(storageRef);
          setCardBackground({ type: bgType, value: url });
        }
      } catch (e) {
        Alert.alert('Upload failed', 'Could not upload background.');
      } finally {
        setUploadingCardBg(false);
      }
    },
    [userData?.uid]
  );

  const handleSave = useCallback(async () => {
    if (!userData?.uid) {
      Alert.alert('Sign in required', 'Please sign in to edit your profile card.');
      return;
    }
    try {
      setSaving(true);
      const db = getFirestore();
      const base = userData.profileCardTemplate || {};
      const templatePayload = sanitizeForFirestore({
        ...base,
        items,
        postContent: base.postContent ?? '',
        location: base.location ?? '',
        cardBackground: cardBackground || null,
        headerTextColor: base.headerTextColor ?? null,
        showInterestsOnCard: showInterestsOnCard !== false,
      });
      await updateDoc(doc(db, 'users', userData.uid), {
        profileCardTemplate: templatePayload,
        updatedAt: serverTimestamp(),
      });
      setUser((prev) => ({ ...prev, profileCardTemplate: templatePayload }));
      refreshEntities?.();
      onSaved?.();
    } catch (error) {
      console.error('Error saving profile card:', error);
      Alert.alert('Save failed', 'Could not save your profile card. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    userData?.uid,
    userData?.profileCardTemplate,
    items,
    cardBackground,
    showInterestsOnCard,
    setUser,
    refreshEntities,
    onSaved,
  ]);

  const accent = PROFILE_CARD_EDITOR_BLUE;
  const styles = useMemo(() => createStyles(embedded, accent), [embedded, accent]);

  return (
    <View style={styles.wrap}>
      <DismissOverlay visible={showTemplates || cardBgPanelOpen} onDismiss={closePanels} />
      <View style={styles.card}>
        <View style={styles.cardBgLayer}>
          {cardBackground?.type === 'color' && cardBackground.value ? (
            <View style={{ flex: 1, backgroundColor: cardBackground.value }} />
          ) : null}
          {cardBackground?.type === 'image' && cardBackground.value ? (
            <Image source={{ uri: cardBackground.value }} style={{ flex: 1 }} resizeMode="cover" />
          ) : null}
          {cardBackground?.type === 'video' && cardBackground.value && Platform.OS === 'web' ? (
            <video
              src={cardBackground.value}
              autoPlay
              loop
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : null}
        </View>

        {showInterestsOnCard && interestItems?.length ? (
          <View style={styles.interestOverlay}>
            <CardInterestsDisplay items={interestItems} emojis={interestEmojis} />
          </View>
        ) : null}

        <View style={[styles.toolbar, { zIndex: 1 }]}>
          <View style={styles.toolbarLeft}>
            <TouchableOpacity
              style={[styles.toolbarBtn, showTemplates && { backgroundColor: accent }]}
              onPress={() => {
                setShowTemplates((p) => !p);
                setCardBgPanelOpen(false);
              }}
            >
              <Ionicons name="grid-outline" size={16} color={showTemplates ? '#fff' : accent} />
              <Text style={[styles.toolbarBtnText, showTemplates && { color: '#fff' }]}>Templates</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toolbarBtn, cardBgPanelOpen && { backgroundColor: accent }]}
              onPress={() => {
                setCardBgPanelOpen((p) => !p);
                setShowTemplates(false);
              }}
            >
              <Ionicons name="color-palette-outline" size={16} color={cardBgPanelOpen ? '#fff' : accent} />
              <Text style={[styles.toolbarBtnText, cardBgPanelOpen && { color: '#fff' }]}>Card BG</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toolbarBtn, showInterestsOnCard && { backgroundColor: accent }]}
              onPress={() => setShowInterestsOnCard((prev) => !prev)}
            >
              <Ionicons name="heart-outline" size={16} color={showInterestsOnCard ? '#fff' : accent} />
              <Text style={[styles.toolbarBtnText, showInterestsOnCard && { color: '#fff' }]}>
                Interests
              </Text>
            </TouchableOpacity>
            {cardBackground ? (
              <TouchableOpacity style={styles.toolbarBtnClear} onPress={() => setCardBackground(null)}>
                <Ionicons name="close-circle" size={16} color={accent} />
                <Text style={styles.toolbarBtnText}>Clear BG</Text>
              </TouchableOpacity>
            ) : null}
            {(showTemplates || cardBgPanelOpen) && (
              <TouchableOpacity style={styles.toolbarBtnClear} onPress={closePanels}>
                <Ionicons name="chevron-up-circle" size={20} color={accent} />
              </TouchableOpacity>
            )}
          </View>
        </View>

        <View style={styles.modeRow} key={`hist-${historyVersion}`}>
          <TouchableOpacity
            style={[styles.toolbarBtn, styles.modeAuxBtn, !canUndo && styles.modeAuxBtnDisabled]}
            onPress={handleUndo}
            disabled={!canUndo}
          >
            <Ionicons name="arrow-undo-outline" size={16} color={!canUndo ? '#9aa' : accent} />
            <Text style={[styles.toolbarBtnText, !canUndo && styles.modeAuxBtnTextDisabled]}>Undo</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toolbarBtn, styles.modeAuxBtn, !canRedo && styles.modeAuxBtnDisabled]}
            onPress={handleRedo}
            disabled={!canRedo}
          >
            <Ionicons name="arrow-redo-outline" size={16} color={!canRedo ? '#9aa' : accent} />
            <Text style={[styles.toolbarBtnText, !canRedo && styles.modeAuxBtnTextDisabled]}>Redo</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toolbarBtn, styles.modeBtn, gridMode === 'resize' && { backgroundColor: accent }]}
            onPress={() => setGridMode((m) => (m === 'move' ? 'resize' : 'move'))}
          >
            <Ionicons
              name={gridMode === 'move' ? 'move-outline' : 'resize-outline'}
              size={16}
              color={gridMode === 'resize' ? '#fff' : accent}
            />
            <Text style={[styles.toolbarBtnText, gridMode === 'resize' && { color: '#fff' }]}>
              {gridMode === 'move' ? 'Move Mode' : 'Resize Mode'}
            </Text>
          </TouchableOpacity>
        </View>
        <MoveModeDesktopNote />

        <View style={styles.gridWithPanelsWrap}>
          {(showTemplates || cardBgPanelOpen) && (
            <View style={styles.panelsOverlay} pointerEvents="box-none">
              {showTemplates && (
                <View style={styles.panel}>
                  <Text style={styles.panelTitle}>Choose a layout template</Text>
                  <View style={styles.templateRow}>
                    {TEMPLATES.map((t) => (
                      <TouchableOpacity
                        key={t.id}
                        style={styles.templateBtn}
                        onPress={() => {
                          setItemsWithHistory((prev) => mergeTemplateWithCurrent(prev, t.build()));
                          setShowTemplates(false);
                        }}
                      >
                        <Ionicons name={t.icon} size={22} color={accent} />
                        <Text style={styles.templateBtnLabel}>{t.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              )}
              {cardBgPanelOpen && (
                <View style={styles.panel}>
                  <View style={styles.panelTabRow}>
                    {['color', 'image', 'video'].map((tab) => (
                      <TouchableOpacity
                        key={tab}
                        style={[styles.panelTab, cardBgTab === tab && { backgroundColor: accent }]}
                        onPress={() => setCardBgTab(tab)}
                      >
                        <Text style={[styles.panelTabText, cardBgTab === tab && { color: '#fff' }]}>
                          {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  {cardBgTab === 'color' && (
                    <CardBgColorPicker
                      swatches={CARD_BG_SWATCHES}
                      selectedColor={
                        cardBackground?.type === 'color' ? cardBackground.value : null
                      }
                      accentColor={accent}
                      onSelectColor={(color) => setCardBackground({ type: 'color', value: color })}
                    />
                  )}
                  {(cardBgTab === 'image' || cardBgTab === 'video') && (
                    <View style={styles.bgUploadRow}>
                      {cardBackground?.type === cardBgTab && cardBackground.value ? (
                        <View style={styles.bgPreviewWrap}>
                          {cardBgTab === 'image' ? (
                            <Image
                              source={{ uri: cardBackground.value }}
                              style={styles.bgPreview}
                              resizeMode="cover"
                            />
                          ) : Platform.OS === 'web' ? (
                            <video
                              src={cardBackground.value}
                              autoPlay
                              loop
                              muted
                              playsInline
                              style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 6 }}
                            />
                          ) : (
                            <View
                              style={[
                                styles.bgPreview,
                                { backgroundColor: '#222', alignItems: 'center', justifyContent: 'center' },
                              ]}
                            >
                              <Ionicons name="videocam" size={28} color="#fff" />
                            </View>
                          )}
                          <TouchableOpacity
                            onPress={() => setCardBackground(null)}
                            style={styles.bgPreviewRemove}
                          >
                            <Ionicons name="close-circle" size={20} color="rgba(0,0,0,0.6)" />
                          </TouchableOpacity>
                        </View>
                      ) : (
                        <TouchableOpacity
                          style={styles.bgUploadBtn}
                          onPress={() => handleCardBgUpload(cardBgTab)}
                          disabled={uploadingCardBg}
                        >
                          {uploadingCardBg ? (
                            <ActivityIndicator size="small" color={accent} />
                          ) : (
                            <>
                              <Ionicons
                                name={cardBgTab === 'video' ? 'videocam-outline' : 'image-outline'}
                                size={20}
                                color={accent}
                              />
                              <Text style={styles.bgUploadBtnText}>Upload {cardBgTab}</Text>
                            </>
                          )}
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </View>
              )}
            </View>
          )}
          <View style={{ zIndex: 1 }}>
            <EditablePostGrid
              items={items}
              setItems={setItemsWithHistory}
              widgetTypes={WIDGET_TYPES}
              onFileUpload={handleFileUpload}
              accentColor={accent}
              cellSize={CELL_SIZE}
              uploadingCellId={uploadingCellId}
              gridBgColor={cardBackground ? 'transparent' : '#fff'}
              interactionMode={gridMode}
            />
          </View>
        </View>
      </View>

      <View style={styles.saveRow}>
        <TouchableOpacity
          style={[styles.postBtn, saving && styles.postBtnDisabled]}
          onPress={handleSave}
          disabled={saving}
          activeOpacity={0.8}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#ffffff" />
          ) : (
            <Text style={styles.postBtnText}>{saveLabel}</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

function createStyles(embedded, accent) {
  return StyleSheet.create({
    wrap: {
      width: '100%',
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: 12,
      padding: 16,
      maxWidth: 400,
      alignSelf: 'center',
      zIndex: 20,
      width: '100%',
      overflow: 'visible',
      position: 'relative',
    },
    cardBgLayer: {
      ...StyleSheet.absoluteFillObject,
      borderRadius: 12,
      overflow: 'hidden',
    },
    interestOverlay: {
      position: 'absolute',
      top: 8,
      right: 8,
      zIndex: 12,
    },
    toolbar: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 8,
      marginBottom: 8,
      minHeight: 35,
      alignItems: 'center',
    },
    toolbarLeft: {
      flexDirection: 'row',
      gap: 8,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    modeBtn: {
      alignSelf: 'flex-end',
    },
    modeRow: {
      width: '100%',
      flexDirection: 'row',
      gap: 8,
      marginBottom: 8,
      flexWrap: 'wrap',
    },
    modeAuxBtn: {
      backgroundColor: embedded ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)',
    },
    modeAuxBtnDisabled: {
      opacity: 0.5,
    },
    modeAuxBtnTextDisabled: {
      color: '#9aa',
    },
    toolbarBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 8,
      backgroundColor: embedded ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.15)',
      borderWidth: 1,
      borderColor: accent,
    },
    toolbarBtnClear: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 8,
      backgroundColor: embedded ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.1)',
    },
    toolbarBtnText: {
      fontFamily: 'VastagoGrotesk',
      fontSize: 11,
      color: accent,
    },
    gridWithPanelsWrap: {
      position: 'relative',
      zIndex: 1,
    },
    panelsOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 11,
      paddingBottom: 8,
    },
    panel: {
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderRadius: 10,
      padding: 12,
      marginBottom: 10,
      maxWidth: 400,
      alignSelf: 'center',
      width: '100%',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 6,
      elevation: 6,
    },
    panelTitle: {
      fontFamily: 'VastagoGrotesk',
      fontSize: 12,
      color: '#555',
      marginBottom: 10,
    },
    panelTabRow: { flexDirection: 'row', gap: 6, marginBottom: 10 },
    panelTab: {
      paddingVertical: 5,
      paddingHorizontal: 12,
      borderRadius: 6,
      backgroundColor: '#f0f0f0',
    },
    panelTabText: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#333' },
    templateRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    templateBtn: {
      alignItems: 'center',
      gap: 4,
      padding: 10,
      borderRadius: 8,
      backgroundColor: '#f5f5f5',
      borderWidth: 1,
      borderColor: '#e0e0e0',
      minWidth: 60,
    },
    templateBtnLabel: { fontFamily: 'VastagoGrotesk', fontSize: 10, color: '#333' },
    swatchGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
    bgSwatch: { width: 32, height: 32, borderRadius: 16 },
    bgSwatchActive: { borderWidth: 3, borderColor: accent },
    hexRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    hexLabel: { fontFamily: 'VastagoGrotesk', fontSize: 14, color: '#555' },
    hexInput: {
      flex: 1,
      fontFamily: 'VastagoGrotesk',
      fontSize: 13,
      color: '#333',
      borderWidth: 1,
      borderColor: '#ddd',
      borderRadius: 6,
      paddingHorizontal: 8,
      paddingVertical: 5,
    },
    hexApplyBtn: { paddingVertical: 5, paddingHorizontal: 12, backgroundColor: accent, borderRadius: 6 },
    hexApplyText: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#fff' },
    bgUploadRow: { alignItems: 'flex-start' },
    bgUploadBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      padding: 12,
      borderRadius: 8,
      borderWidth: 1.5,
      borderColor: accent,
      borderStyle: 'dashed',
    },
    bgUploadBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 13, color: accent },
    bgPreviewWrap: { position: 'relative' },
    bgPreview: { width: 120, height: 80, borderRadius: 6 },
    bgPreviewRemove: { position: 'absolute', top: -8, right: -8 },
    saveRow: {
      alignItems: 'center',
      marginTop: embedded ? 16 : 24,
      marginBottom: embedded ? 0 : 32,
    },
    postBtn: {
      paddingVertical: 10,
      paddingHorizontal: 32,
      borderRadius: 999,
      backgroundColor: '#000000',
      minWidth: 120,
      alignItems: 'center',
      justifyContent: 'center',
    },
    postBtnDisabled: {
      opacity: 0.7,
    },
    postBtnText: {
      fontFamily: 'Playpen Sans',
      fontSize: embedded ? 16 : 20,
      marginBottom: 0,
      color: '#ffffff',
    },
  });
}
